import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ModuleKey, MODULE_DEFAULT_ENABLED } from '../constants/module-keys'
import { useModuleFlag } from '../utils/featureFlags'

interface ProtectedRouteProps {
  children: React.ReactNode
  /**
   * Module key from feature flags. When the module is disabled,
   * user is redirected to redirectTo.
   */
  moduleKey: ModuleKey
  /**
   * Default value when the module flag is not yet loaded or undefined.
   * Defaults to MODULE_DEFAULT_ENABLED[moduleKey].
   */
  defaultEnabled?: boolean
  /**
   * Where to redirect when access is not allowed (module disabled).
   */
  redirectTo?: string
}

/**
 * Protects a route by a module feature flag. When the module is disabled
 * (e.g. workflows turned off in package/license), redirects to dashboard
 * instead of rendering the page. Use for routes that are hidden in the
 * sidebar when the module is disabled but would still be reachable via URL.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  moduleKey,
  defaultEnabled,
  redirectTo = '/dashboard',
}) => {
  const location = useLocation()
  const isEnabled = useModuleFlag(
    moduleKey,
    defaultEnabled ?? MODULE_DEFAULT_ENABLED[moduleKey]
  )

  if (!isEnabled) {
    return <Navigate to={redirectTo} replace state={{ from: location, moduleDisabled: moduleKey }} />
  }

  return <>{children}</>
}
